/**
 * Umami page view tracking for Inertia navigation.
 *
 * The tracker is loaded with data-auto-track="false" (see
 * ApplicationHelper#umami_script_tag), so Umami never fires page views of its
 * own. We send them here instead, which buys two things its automatic tracking
 * cannot do:
 *
 *   1. Record ids are masked out of the URL, so /inventory/<uuid> is reported
 *      as /inventory/[id] rather than one row per asset.
 *   2. Inertia's filter/sort/pagination visits are collapsed. Every list page
 *      navigates via router.get(path, { search, sort, direction, page }), so
 *      automatic tracking would log a page view per sort click and per
 *      debounced keystroke.
 */

// Any path segment that looks like a UUID is a record id. Matching on shape
// rather than on a list of routes means new routes are covered the day they
// are added, with no change here.
const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Query params that identify a genuinely distinct page rather than a filter.
// asset_type switches Inventory between kegs/fridges/cylinders/taps, which is
// worth splitting in the reports; search/sort/direction/page are not.
const TRACKED_QUERY_PARAMS = ["asset_type"]

// lib/pageTitle.ts sets document.title before every page view is reported, so
// this is only a backstop against reporting a blank title if that ever fails.
const FALLBACK_TITLE = "BierTrack"

let lastTrackedUrl: string | null = null

type UmamiPayload = Record<string, unknown>

/**
 * True when the tracker script tag is in the document. In development, test,
 * and CI the ENV vars are unset, the helper renders nothing, and every
 * function here becomes an immediate no-op.
 */
function trackerPresent(): boolean {
  return document.querySelector("script[data-website-id]") !== null
}

/** The URL to report: id-masked path plus allowlisted query params. */
function trackedUrl(): string {
  const path = window.location.pathname
    .split("/")
    .map((segment) => (UUID_SEGMENT.test(segment) ? "[id]" : segment))
    .join("/")

  const current = new URLSearchParams(window.location.search)
  const tracked = new URLSearchParams()
  for (const key of TRACKED_QUERY_PARAMS) {
    const value = current.get(key)
    if (value) tracked.set(key, value)
  }

  const query = tracked.toString()
  return query ? `${path}?${query}` : path
}

/**
 * Run `callback` once window.umami exists. The tracker tag is `defer`, so on a
 * cold load it may not have executed by the time the first page view fires --
 * and that first view is the one we can least afford to drop. Only polls when
 * the tag is actually present, so this never spins in development.
 */
function withUmami(callback: (umami: NonNullable<Window["umami"]>) => void): void {
  if (window.umami) {
    callback(window.umami)
    return
  }

  let attempts = 0
  const poll = () => {
    if (window.umami) {
      callback(window.umami)
      return
    }
    // ~5s. If the tracker still has not loaded it is blocked or down; drop the
    // view rather than leaking a timer for the life of the session.
    if (++attempts > 100) return
    window.setTimeout(poll, 50)
  }
  window.setTimeout(poll, 50)
}

/**
 * Report a page view for the current location. Safe to call on every Inertia
 * navigation: repeat URLs are dropped, which is what collapses a filtering
 * session into a single view and makes a double call on initial load harmless.
 */
export function trackPageView(): void {
  if (!trackerPresent()) return

  // Wait a frame so React has committed Inertia's <title> update; otherwise the
  // page view is reported with the previous page's title.
  requestAnimationFrame(() => {
    const url = trackedUrl()
    if (url === lastTrackedUrl) return
    lastTrackedUrl = url

    withUmami((umami) => {
      umami.track((payload: UmamiPayload) => ({
        ...payload,
        url,
        title: document.title || FALLBACK_TITLE,
      }))
    })
  })
}
